import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { t } from '../i18n';
import Modal from './Modal';
import { toast } from 'sonner';
import { MessageSquareWarning, Loader2, CheckCircle2, XCircle, HelpCircle, Quote, History, Sparkles } from 'lucide-react';

/**
 * Document Detail viewer + Review & Self-Correction.
 * Heads (own dept) and admins can flag any block (summary / metadata field);
 * the AI re-checks the ORIGINAL SOURCE and proposes a correction with proof.
 */
export default function DocumentDetailModal({ docId, onClose, initialPage = null, highlightText = null }) {
  const { lang, auth } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState('full');
  const [reviewRef, setReviewRef] = useState(null); // field_ref being reviewed
  const [corrections, setCorrections] = useState([]);

  const role = auth?.user?.role;
  const canReview = data && (role === 'super_admin' ||
    (role === 'dept_head' && auth?.user?.department === data.document.department));

  const load = useCallback(() => {
    axios.get(`${API}/documents/${docId}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || 'Failed to load document'));
  }, [docId]);

  const loadCorrections = useCallback(() => {
    axios.get(`${API}/documents/${docId}/corrections`)
      .then(r => setCorrections(r.data)).catch(() => {});
  }, [docId]);

  useEffect(() => { setData(null); setErr(null); load(); loadCorrections(); }, [load, loadCorrections]);

  return (
    <Modal onClose={onClose} testid="doc-detail-modal" widthClass="max-w-4xl">
      {!data && !err && (
        <div className="px-8 py-10 space-y-3">
          <div className="skeleton h-6 w-2/3 rounded-sm" />
          <div className="skeleton h-4 w-1/3 rounded-sm" />
          <div className="skeleton h-32 w-full rounded-sm" />
        </div>
      )}
      {err && (
        <div className="px-8 py-10">
          <div className="text-red-500 text-[14px]">{err}</div>
          <button onClick={onClose} className="btn-secondary mt-4">{t(lang, 'common.back')}</button>
        </div>
      )}
      {data && (
        <>
          <div className="px-8 pt-7 pb-4 border-b border-white/10 flex-shrink-0">
            <div className="data-label accent-line text-[#DDA079] mb-2">{data.document.type}</div>
            <h2 className="font-headings text-2xl text-white mb-3">{data.document.name}</h2>
            <div className="flex gap-2 flex-wrap">
              <span className="v-badge cost">v{data.document.version}</span>
              <span className="v-badge cost">{data.document.department}</span>
              <span className="v-badge cost">{data.document.pages} pages</span>
              {data.document.language !== 'en' && <span className="v-badge conf">{data.document.language.toUpperCase()}</span>}
              {data.document.metadata?.ingested_via === 'pdf_upload' && <span className="v-badge ai">PDF</span>}
              {data.document.source === 'azure_blob' && <span className="v-badge ai">BLOB · {data.document.folder}</span>}
              {data.document.ocr?.engine && <span className="v-badge conf">OCR</span>}
            </div>
            <div className="mt-3 text-[11px] font-mono text-slate-500">
              Uploaded {new Date(data.document.uploaded_at).toLocaleDateString('en-IN')} by {data.document.uploaded_by}
              {data.document.blob_path && <> · from <span className="text-slate-400">{data.document.blob_path}</span></>}
            </div>
          </div>

          <div className="flex border-b border-white/10 px-8 overflow-x-auto flex-shrink-0">
            {[
              { k: 'full', label: 'Full Document' },
              { k: 'intel', label: 'Summary' },
              { k: 'metadata', label: 'Metadata' },
            ].map(item => (
              <button
                key={item.k}
                data-testid={`doc-tab-${item.k}`}
                onClick={() => setTab(item.k)}
                className={`px-4 py-3 text-[12px] font-mono uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
                  tab === item.k ? 'border-[#C17F59] text-white' : 'border-transparent text-slate-400 hover:text-white'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto px-8 py-6">
            {tab === 'full' && <FullDocumentView document={data.document} chunks={data.chunks} initialPage={initialPage} highlightText={highlightText} />}

            {tab === 'intel' && (
              <IntelligenceTab docId={docId} document={data.document} onGenerated={load} />
            )}

            {tab === 'metadata' && (
              Object.keys(data.document.metadata || {}).length > 0 ? (
                <div className="space-y-1 text-[12px]">
                  {Object.entries(data.document.metadata).map(([k, v]) => (
                    <div key={k}>
                      <div className="flex items-center justify-between border-b border-white/5 py-1.5 gap-2">
                        <span className="text-slate-500 font-mono">{k}</span>
                        <span className="text-white text-right flex-1">{String(v)}</span>
                        {canReview && (
                          <FlagButton active={reviewRef === `metadata.${k}`}
                            onClick={() => setReviewRef(reviewRef === `metadata.${k}` ? null : `metadata.${k}`)}
                            testid={`flag-meta-${k}`} />
                        )}
                      </div>
                      {reviewRef === `metadata.${k}` && (
                        <ReviewPanel docId={docId} fieldRef={`metadata.${k}`}
                          onDone={() => { setReviewRef(null); load(); loadCorrections(); }} />
                      )}
                    </div>
                  ))}
                </div>
              ) : <div className="text-slate-500 text-[13px]">No metadata available.</div>
            )}
          </div>

          <div className="px-8 py-4 border-t border-white/10 flex-shrink-0">
            <button onClick={onClose} className="btn-secondary" data-testid="doc-modal-close">{t(lang, 'common.back')}</button>
          </div>
        </>
      )}
    </Modal>
  );
}

function IntelligenceTab({ docId, document, onGenerated }) {
  const [busy, setBusy] = useState(false);
  const intel = document.intelligence;

  const generate = async (force = false) => {
    setBusy(true);
    try {
      await axios.post(`${API}/documents/${docId}/intelligence${force ? '?force=true' : ''}`);
      toast.success('Document intelligence generated');
      onGenerated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally { setBusy(false); }
  };

  if (!intel) {
    return (
      <div className="text-center py-10" data-testid="intel-empty">
        <p className="text-slate-400 text-[13px] mb-4">
          No structured intelligence yet for this document.
        </p>
        <button data-testid="generate-intel" onClick={() => generate(false)} disabled={busy}
          className="btn-primary flex items-center gap-2 mx-auto">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {busy ? 'Analysing source…' : 'Generate intelligence'}
        </button>
      </div>
    );
  }

  const meta = intel.metadata || {};
  const metaRows = [
    ['Parties', Array.isArray(meta.parties) ? meta.parties.join(' · ') : meta.parties],
    ['Effective Date', meta.effective_date], ['Expiry Date', meta.expiry_date],
    ['Term', meta.term], ['Notice Period', meta.notice_period],
    ['Value / Amount', meta.value_or_amount],
    ['Governing Authority', meta.governing_law_or_authority],
    ['Reference No.', meta.reference_number],
  ].filter(([, v]) => v);

  return (
    <div className="space-y-6" data-testid="intel-view">
      <div className="flex items-start justify-between gap-3">
        <div>
          {intel.title && <div className="text-white text-[14px] font-medium">{intel.title}</div>}
          {intel.document_type && (
            <span className="inline-block mt-1 border border-[#C17F59]/40 text-[#DDA079] rounded-sm px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider">
              {intel.document_type}
            </span>
          )}
        </div>
        <button data-testid="regenerate-intel" onClick={() => generate(true)} disabled={busy}
          className="btn-secondary flex items-center gap-1.5 text-[11px] flex-shrink-0">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {busy ? 'Re-analysing…' : 'Regenerate'}
        </button>
      </div>

      <section>
        <div className="data-label accent-line text-[#DDA079] mb-2">Executive Summary</div>
        <p className="text-slate-200 text-[13.5px] leading-relaxed whitespace-pre-wrap">{intel.executive_summary}</p>
      </section>

      {metaRows.length > 0 && (
        <section data-testid="intel-metadata">
          <div className="data-label accent-line text-[#DDA079] mb-2">Extracted Metadata</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            {metaRows.map(([label, value]) => (
              <div key={label} className="border-b border-white/5 pb-1.5">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
                <div className="text-slate-200 text-[12.5px]">{value}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {intel.key_points?.length > 0 && (
        <section>
          <div className="data-label accent-line text-[#DDA079] mb-2">Key Points — exact facts</div>
          <ul className="space-y-1.5">
            {intel.key_points.map((k, i) => (
              <li key={i} className="text-slate-200 text-[13px] flex gap-2">
                <span className="text-[#C17F59] mt-0.5">▪</span>
                <span>
                  {typeof k === 'string' ? k : k.point}
                  {k?.page ? <span className="text-[10px] font-mono text-[#DDA079] ml-1.5">p.{k.page}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {intel.important_clauses?.length > 0 && (
        <section>
          <div className="data-label accent-line text-[#DDA079] mb-2">Important Clauses</div>
          <div className="space-y-1.5">
            {intel.important_clauses.map((c, i) => (
              <div key={i} className="border border-white/10 rounded-sm px-2.5 py-1.5">
                <span className="text-[12.5px] text-slate-100">{c.text}</span>
                <span className="text-[10px] font-mono text-[#DDA079] ml-1.5">p.{c.page}</span>
                {c.note && <p className="text-slate-400 text-[11.5px] mt-0.5">{c.note}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {intel.section_summaries?.length > 0 && (
        <section>
          <div className="data-label accent-line text-[#DDA079] mb-2">Section Summaries</div>
          <div className="space-y-2">
            {intel.section_summaries.map((s, i) => (
              <div key={i} className="border-l-2 border-[#C17F59]/40 pl-3">
                <div className="text-[11px] font-mono text-[#DDA079]">{s.section} · p.{s.page}</div>
                <p className="text-slate-300 text-[12.5px]">{s.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {intel.detailed_summary && (
        <section>
          <div className="data-label accent-line text-[#DDA079] mb-2">Detailed Summary</div>
          <p className="text-slate-300 text-[13px] leading-relaxed whitespace-pre-wrap">{intel.detailed_summary}</p>
        </section>
      )}

      {intel.faq_candidates?.length > 0 && (
        <section>
          <div className="data-label accent-line text-[#DDA079] mb-2">FAQ Candidates (auto-generated)</div>
          <div className="space-y-2">
            {intel.faq_candidates.map((f, i) => (
              <div key={i} className="border border-white/5 rounded-sm p-3">
                <div className="text-white text-[12.5px] mb-1">
                  Q: {f.q}
                  {f.page ? <span className="text-[10px] font-mono text-[#DDA079] ml-1.5">p.{f.page}</span> : null}
                </div>
                <div className="text-slate-400 text-[12px]">A: {f.a}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {intel.source_citations?.length > 0 && (
        <section data-testid="intel-citations">
          <div className="data-label accent-line text-[#DDA079] mb-2">Source Citations — page map</div>
          <div className="flex flex-wrap gap-1.5">
            {intel.source_citations.map((c, i) => (
              <span key={i} className="border border-white/10 rounded-sm px-2 py-1 text-[11px] text-slate-300">
                <span className="font-mono text-[#DDA079]">p.{c.page}</span> {c.topic}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const FlagButton = ({ active, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    title="Flag for AI re-check against the source"
    className={`p-1.5 rounded-sm border transition-colors flex-shrink-0 ${
      active ? 'border-[#C17F59] text-[#DDA079]' : 'border-white/10 text-slate-500 hover:text-[#DDA079] hover:border-[#C17F59]/50'
    }`}
  >
    <MessageSquareWarning size={13} />
  </button>
);

function ReviewPanel({ docId, fieldRef, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const recheck = async () => {
    if (!note.trim()) { toast.error('Describe what looks wrong'); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/documents/${docId}/review`, { field_ref: fieldRef, note });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-check failed');
    } finally { setBusy(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/documents/${docId}/review/${result.id}/apply`);
      toast.success(`Correction applied · ${data.cascade.cache_entries} cached answers purged, ${data.cascade.faqs_reverted} FAQs reverted to pending`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
      setApplying(false);
    }
  };

  const dismiss = async () => {
    try { await axios.post(`${API}/documents/${docId}/review/${result.id}/dismiss`); } catch { /* noop */ }
    onDone();
  };

  const OutcomeIcon = result?.outcome === 'ai_wrong' ? XCircle : result?.outcome === 'ai_right' ? CheckCircle2 : HelpCircle;
  const outcomeColor = result?.outcome === 'ai_wrong' ? 'text-amber-400' : result?.outcome === 'ai_right' ? 'text-emerald-400' : 'text-slate-400';
  const outcomeLabel = result?.outcome === 'ai_wrong' ? 'Stored value is wrong — source contradicts it'
    : result?.outcome === 'ai_right' ? 'Stored value matches the source'
    : 'Source is unclear — manual verification needed';

  return (
    <div className="mt-2 mb-3 border border-[#C17F59]/30 bg-[#C17F59]/[0.04] rounded-sm p-4" data-testid="review-panel">
      {!result ? (
        <>
          <div className="data-label text-[#DDA079] mb-2">Flag · AI will re-check the original source</div>
          <textarea
            data-testid="review-note"
            value={note}
            onChange={e => setNote(e.target.value)}
            className="v-input min-h-[64px] text-[13px]"
            placeholder='e.g. "that maternity figure looks wrong to me"'
          />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={onDone} className="btn-secondary text-[11px]">Cancel</button>
            <button data-testid="review-submit" onClick={recheck} disabled={busy} className="btn-primary text-[11px] flex items-center gap-1.5">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <MessageSquareWarning size={12} />}
              {busy ? 'Re-checking source…' : 'Re-check against source'}
            </button>
          </div>
        </>
      ) : (
        <div data-testid="review-result">
          <div className={`flex items-center gap-2 mb-2 ${outcomeColor}`}>
            <OutcomeIcon size={15} />
            <span className="text-[13px] font-medium">{outcomeLabel}</span>
          </div>
          <p className="text-slate-300 text-[12.5px] mb-3">{result.ai_finding}</p>
          {result.source_quote && (
            <div className="border-l-2 border-[#C17F59]/50 pl-3 mb-3">
              <div className="flex items-center gap-1.5 data-label mb-1">
                <Quote size={10} /> Source proof{result.source_page ? ` · page ${result.source_page}` : ''}
              </div>
              <p className="text-slate-400 text-[12px] italic">"{result.source_quote}"</p>
            </div>
          )}
          {result.outcome === 'ai_wrong' && result.new_value && (
            <div className="grid grid-cols-2 gap-2 text-[12px] mb-3">
              <div className="border border-red-500/20 rounded-sm p-2">
                <div className="data-label mb-1">Stored (wrong)</div>
                <div className="text-red-300">{String(result.old_value).slice(0, 200)}</div>
              </div>
              <div className="border border-emerald-500/20 rounded-sm p-2">
                <div className="data-label mb-1">Correction</div>
                <div className="text-emerald-300">{String(result.new_value).slice(0, 200)}</div>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 items-center">
            {confirming && (
              <span className="text-[11px] text-amber-300 mr-auto" data-testid="cascade-warning">
                This will purge {result.affected?.cached_answers ?? 0} cached answer(s) and revert {result.affected?.approved_faqs ?? 0} approved FAQ(s) to pending. Confirm?
              </span>
            )}
            <button data-testid="review-dismiss" onClick={dismiss} className="btn-secondary text-[11px]">Dismiss</button>
            {result.outcome === 'ai_wrong' && result.new_value && (
              confirming ? (
                <button data-testid="review-apply-confirm" onClick={apply} disabled={applying} className="btn-primary text-[11px] flex items-center gap-1.5">
                  {applying && <Loader2 size={12} className="animate-spin" />} Yes, apply correction
                </button>
              ) : (
                <button data-testid="review-apply" onClick={() => setConfirming(true)} className="btn-primary text-[11px]">
                  Apply correction
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function FullDocumentView({ document, chunks, initialPage = null, highlightText = null }) {
  const scrolled = useRef(false);

  const hlNeedle = highlightText ? highlightText.replace(/\s+/g, ' ').slice(0, 40).toLowerCase() : null;
  const isHighlighted = (text) => hlNeedle && text && text.replace(/\s+/g, ' ').toLowerCase().includes(hlNeedle);

  // Target page: prefer the page whose chunk actually contains the cited snippet,
  // else the cited page, else the nearest existing page (re-extraction can renumber pages).
  const availablePages = [...new Set((chunks || []).map(c => c.page))].sort((a, b) => a - b);
  let targetPage = null;
  if (initialPage != null && availablePages.length) {
    const match = (chunks || []).find(c => isHighlighted(c.text));
    if (match) targetPage = match.page;
    else if (availablePages.includes(initialPage)) targetPage = initialPage;
    else targetPage = availablePages.reduce((best, p) =>
      Math.abs(p - initialPage) < Math.abs(best - initialPage) ? p : best, availablePages[0]);
  }

  useEffect(() => {
    if (targetPage == null || scrolled.current) return;
    const timer = setTimeout(() => {
      const el = window.document.getElementById(`doc-page-${targetPage}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        scrolled.current = true;
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [targetPage, chunks]);

  const usePageView = initialPage != null && chunks && chunks.length > 0;
  if (!usePageView && document.full_text && document.full_text.trim()) {
    return (
      <div className="text-slate-200 text-[13.5px] leading-[1.7] whitespace-pre-wrap font-sans">
        {document.full_text}
      </div>
    );
  }
  if (!chunks || chunks.length === 0) {
    return (
      <div className="text-slate-500 text-[13px] space-y-3">
        <p>No extracted document text is stored for this entry.</p>
        <p className="text-[12px]">
          Seeded reference documents contain only key indexed passages, not the full original text.
          For full original content, upload the source PDF.
        </p>
      </div>
    );
  }
  const byPage = {};
  for (const c of chunks) {
    if (!byPage[c.page]) byPage[c.page] = [];
    byPage[c.page].push(c);
  }
  const pages = Object.keys(byPage).map(Number).sort((a, b) => a - b);
  return (
    <div className="space-y-6">
      {pages.map(pg => (
        <div key={pg} id={`doc-page-${pg}`}
          className={`border-l-2 pl-4 ${pg === targetPage ? 'border-[#C17F59]' : 'border-[#C17F59]/40'}`}>
          <div className={`data-label mb-2 ${pg === targetPage ? 'text-[#DDA079]' : ''}`}>
            Page {pg}{pg === targetPage ? ' · cited source' : ''}
          </div>
          {byPage[pg].map((c, i) => (
            <div key={i} className={`mb-4 ${isHighlighted(c.text) ? 'bg-[#C17F59]/[0.08] border border-[#C17F59]/40 rounded-sm p-3 -ml-3' : ''}`}
              data-testid={isHighlighted(c.text) ? 'cited-passage-highlight' : undefined}>
              <div className="text-[11px] font-mono text-[#DDA079] mb-1">§ {c.section}</div>
              <p className="text-slate-200 text-[13.5px] leading-[1.7]">{c.text}</p>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
